import { useHydrateFileRecordEntity } from "@/api/fileRecords";
import { ChatFileRecordCardShell } from "@/components/ChatBubble/components/ChatFileRecordCardShell";
import { Skeleton } from "@/components/ui/skeleton";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { cn } from "@/utils";
import { EntityCard } from "@cohesive/ui-components";

interface MessageFileRecordsProps {
  fileRecordIds: string[] | undefined;
  flowSessionId: string;
  align?: "start" | "end";
  className?: string;
}

function MessageFileRecordItem({
  id,
  flowSessionId,
}: {
  id: string;
  flowSessionId: string;
}) {
  const openFileRecord = useOpenFileRecord();
  const { entity, isLoading, isError } = useHydrateFileRecordEntity(id);

  if (isLoading) {
    return <Skeleton className="h-40 w-full rounded-2xl" />;
  }
  if (!entity || isError) {
    return null;
  }

  return (
    // The shell gives the card the detail pane's kebab + right-click menu
    // (tier-aware: container file / reference / workspace file).
    <ChatFileRecordCardShell fileRecordId={id}>
      {({ menuSlot }) => (
        <EntityCard
          entity={entity}
          onClick={() => openFileRecord(id, flowSessionId)}
          menuSlot={menuSlot}
        />
      )}
    </ChatFileRecordCardShell>
  );
}

/**
 * Renders message attachments as a grid of `EntityCard` file_record entities.
 */
export function MessageFileRecords({
  fileRecordIds,
  flowSessionId,
  align = "start",
  className,
}: MessageFileRecordsProps) {
  if (!fileRecordIds || fileRecordIds.length === 0) return null;

  return (
    <div
      className={cn(
        "grid gap-2 mb-2 w-full max-w-[520px]",
        align === "end" && "ml-auto",
        className,
      )}
      style={{
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      }}
    >
      {fileRecordIds.map((id) => (
        <MessageFileRecordItem key={id} id={id} flowSessionId={flowSessionId} />
      ))}
    </div>
  );
}
